import appLogger from "../../../infra/logger/appLogger";
import { isRecord } from "./rulebookDraftNormalization";
const logger = appLogger.withTag("RulebookEditor");
/** The retired per-owner draft → server link map prefix (`:<userId>`), read once to fold into the drafts. */
export const LEGACY_SERVER_IDS_STORAGE_KEY = "rulebook.editor.serverIds.v1";
const legacyServerIdsKey = (ownerId) => `${LEGACY_SERVER_IDS_STORAGE_KEY}:${ownerId ?? "anonymous"}`;
// Legacy link-map keys already folded in this session, per storage (one per owner); keeps the migration from re-reading.
const migratedLegacyKeys = new WeakMap();
const markLegacyKeyMigrated = (storage, legacyKey) => {
    const migrated = migratedLegacyKeys.get(storage) ?? new Set();
    migrated.add(legacyKey);
    migratedLegacyKeys.set(storage, migrated);
};
/**
 * One-time fold of the retired draft → server link map into the drafts it named, for the same owner as the drafts
 * key just read: matching drafts get `rulebook_id`, the drafts map is persisted once under `draftsKey`, and the
 * legacy key removed (a tombstone write for storages without `removeItem`). A corrupt legacy payload is simply
 * dropped — its drafts then publish as new Rulebooks. When the drafts-map persist fails the key stays for the
 * next read to retry, so links are never lost to a transient storage error.
 */
export function migrateLegacyServerIds({ storage, drafts, draftsKey, ownerId, }) {
    const legacyKey = legacyServerIdsKey(ownerId);
    const migrated = migratedLegacyKeys.get(storage);
    if (migrated?.has(legacyKey)) {
        return false;
    }
    const raw = storage.getItem(legacyKey);
    if (raw === null) {
        return false;
    }
    let linked = 0;
    try {
        const parsed = JSON.parse(raw);
        if (isRecord(parsed)) {
            Object.entries(parsed).forEach(([draftId, rulebookId]) => {
                const draft = drafts[draftId];
                if (typeof rulebookId === "string" && draft !== undefined) {
                    drafts[draftId] = { ...draft, rulebook_id: rulebookId };
                    linked += 1;
                }
            });
        }
    }
    catch {
        logger.warn("Legacy rulebook server-id map is corrupt; dropping it");
    }
    if (linked > 0) {
        try {
            storage.setItem(draftsKey, JSON.stringify(drafts));
        }
        catch (error) {
            logger.warn("Could not persist the migrated rulebook draft links", error);
            // Leave the legacy key — and it unmarked — so the next read retries the fold.
            return false;
        }
    }
    try {
        if (storage.removeItem === undefined) {
            storage.setItem(legacyKey, "null");
        }
        else {
            storage.removeItem(legacyKey);
        }
    }
    catch (error) {
        logger.warn("Could not remove the migrated rulebook server-id map", error);
        return false;
    }
    markLegacyKeyMigrated(storage, legacyKey);
    return linked > 0;
}
